import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Mail, ArrowLeft, Check } from 'lucide-react';
import authService from '../../services/authService';
import { useTheme } from '../../contexts/ThemeContext';
import SEO from '../SEO';
import LandingNav from '../Layout/LandingNav';
import LandingFooter from '../Layout/LandingFooter';

function ForgotPasswordPage() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { theme } = useTheme();
  const d = theme === 'dark';

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm();

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      await authService.forgotPassword(data.email);
      setIsSubmitted(true);
      toast.success('Password reset instructions sent!');
    } catch (err) {
      toast.error(err.message || 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${d ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
      <SEO
        title="Reset password"
        description="Reset your PaperBrainz account password."
        path="/forgot-password"
        noindex
      />
      <LandingNav />

      <div className="flex-1 flex items-center justify-center px-4 pt-44 pb-32">
        <div className="relative z-10 w-full max-w-md space-y-8">
          {isSubmitted ? (
            <>
              {/* Success card */}
              <div className="">
                <div className="text-center">
                  <div className="flex justify-center mb-6">
                    <div className={`w-14 h-14 rounded-[14px] flex items-center justify-center ${d ? 'bg-emerald-500/[0.08]' : 'bg-emerald-50'}`}>
                      <Check className={`w-6 h-6 ${d ? 'text-emerald-400' : 'text-emerald-500'}`} />
                    </div>
                  </div>
                  <h2 className={`text-3xl md:text-4xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'}`}>
                    Check Your Email
                  </h2>
                  <p className={`mt-2 text-sm ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                    We've sent password reset instructions to
                  </p>
                  <p className={`mt-1 text-sm font-medium ${d ? 'text-blue-400' : 'text-blue-600'}`}>
                    {getValues('email')}
                  </p>
                </div>

                <div className={`mt-6 rounded-[12px] border p-3.5 ${d ? 'bg-white/[0.02] border-white/[0.06]' : 'bg-gray-50 border-gray-100'}`}>
                  <p className={`text-sm font-medium mb-2 ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                    Didn't receive the email?
                  </p>
                  <ul className={`text-sm space-y-1 list-disc list-inside ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                    <li>Check your spam folder</li>
                    <li>Make sure you entered the correct email</li>
                    <li>Wait a few minutes and check again</li>
                  </ul>
                </div>

                <div className="mt-6 flex flex-col gap-3">
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className={`
                      w-full flex items-center justify-center py-3 px-4 rounded-[12px] text-sm font-medium border transition-all duration-200
                      ${d ? 'border-white/[0.08] text-gray-300 hover:bg-white/[0.04]' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}
                    `}
                  >
                    Try Another Email
                  </button>

                  <Link
                    to="/login"
                    className={`
                      w-full flex items-center justify-center gap-2 py-3 px-4 rounded-[12px] text-sm font-medium transition-all duration-200
                      ${d ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'}
                    `}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Sign In
                  </Link>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Form card */}
              <div className="">
                <div className="text-center mb-10">
                  <h2 className={`text-3xl md:text-4xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'}`}>
                    Forgot Password?
                  </h2>
                  <p className={`mt-2 text-sm ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                    No worries! Enter your email and we'll send you reset instructions.
                  </p>
                </div>

                <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <Mail className={`h-4.5 w-4.5 ${d ? 'text-gray-600' : 'text-gray-400'}`} />
                      </div>
                      <input
                        {...register('email', {
                          required: 'Email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Please enter a valid email address',
                          },
                        })}
                        type="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        className={`
                          block w-full pl-10 pr-3 py-3 rounded-[12px] border text-sm transition-colors
                          ${errors.email
                            ? d ? 'border-red-500/40 focus:border-red-500' : 'border-red-300 focus:border-red-500'
                            : d ? 'border-white/[0.08] focus:border-blue-500/50' : 'border-gray-200 focus:border-blue-400'
                          }
                          ${d ? 'bg-white/[0.03] text-white placeholder-gray-600' : 'bg-gray-100 text-gray-900 placeholder-gray-400'}
                          focus:outline-none focus:ring-1 ${errors.email ? 'focus:ring-red-500/30' : 'focus:ring-blue-500/30'}
                        `}
                      />
                    </div>
                    {errors.email && (
                      <p className={`mt-1.5 text-xs ${d ? 'text-red-400' : 'text-red-500'}`}>{errors.email.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`
                      w-full flex items-center justify-center py-3 px-4 rounded-[12px] text-sm font-medium transition-all duration-200
                      ${isLoading
                        ? d ? 'bg-white/10 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : d ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'
                      }
                    `}
                  >
                    {isLoading ? (
                      <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Send Reset Instructions'
                    )}
                  </button>
                </form>
              </div>

              {/* Back to login card */}
              <Link
                to="/login"
                className={`block rounded-[16px] border p-4 text-center text-sm transition-colors duration-300 ${
                  d ? 'bg-[#111] border-white/[0.06] text-gray-400 hover:text-gray-300' : 'bg-white border-gray-200 shadow-sm text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="inline-flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Sign In
                </span>
              </Link>
            </>
          )}
        </div>
      </div>

      <LandingFooter />
    </div>
  );
}

export default ForgotPasswordPage;
